import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Select,
  Box,
  useToast,
  IconButton,
  Textarea,
  FormControl,
  FormLabel,
  Badge,
  Divider,
  RadioGroup,
  Radio,
  Flex
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import api from '../../../services/api';

const CallsForm = ({ leadId, onUpdate, initialCallData = null }) => {
  const [calls, setCalls] = useState([]);
  const [newCall, setNewCall] = useState({
    outcome: '',
    notes: '',
    rescheduledTo: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [editingCallId, setEditingCallId] = useState(null);
  const [editedNotes, setEditedNotes] = useState('');
  const toast = useToast();

  const fetchCalls = async () => {
    try {
      const response = await api.get(`/api/call-logs/lead/${leadId}`);
      const callLogs = response.data.callLogs || [];
      
      console.log('[DEBUG] Processing call logs in CallsForm:', callLogs.map(call => ({
        timestamp: call.timestamp,
        timestampParsed: new Date(call.timestamp).toString(),
        timestampLocal: new Date(call.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
        rescheduledTo: call.rescheduledTo,
        rescheduledToParsed: call.rescheduledTo ? new Date(call.rescheduledTo).toString() : null,
        rescheduledToLocal: call.rescheduledTo ? new Date(call.rescheduledTo).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : null,
        browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        browserOffset: -new Date().getTimezoneOffset()/60
      })));
      
      setCalls(callLogs);
    } catch (error) {
      console.error('Error fetching calls:', error);
      toast({
        title: 'Error fetching calls',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    }
  };

  useEffect(() => {
    // If initialCallData is provided, add it automatically
    if (initialCallData && leadId) {
      handleAddCall(initialCallData);
    }
  }, [initialCallData, leadId]);

  useEffect(() => {
    if (leadId) {
      fetchCalls();
    }
  }, [leadId]);

  const handleAddCall = async (callDataOverride = null) => {
    if (!callDataOverride && !newCall.notes.trim()) return;
    if (!callDataOverride && !newCall.outcome) {
      toast({
        title: 'Outcome is required',
        description: 'Please select an outcome for the call',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const callData = {
        outcome: callDataOverride?.outcome || newCall.outcome,
        notes: callDataOverride?.notes || newCall.notes,
        rescheduledTo: callDataOverride?.rescheduledTo || newCall.rescheduledTo,
        leadId,
        timestamp: new Date().toISOString()
      };

      // If rescheduled, ensure rescheduledTo is provided
      if (newCall.outcome === 'rescheduled' && !newCall.rescheduledTo) {
        throw new Error('Reschedule date is required when outcome is rescheduled');
      }

      await api.post('/api/call-logs', callData);
      setNewCall({
        outcome: '',
        notes: '',
        rescheduledTo: '',
      });
      fetchCalls();
      toast({
        title: 'Call log added',
        status: 'success',
        duration: 3000,
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error adding call log:', error);
      toast({
        title: 'Error adding call log',
        description: error.response?.data?.message || error.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCall = async (callId, updates) => {
    try {
      await api.put(`/api/call-logs/${callId}`, updates);
      fetchCalls();
      if (editingCallId === callId) {
        setEditingCallId(null);
      }
      toast({
        title: 'Call log updated',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error updating call log:', error);
      toast({
        title: 'Error updating call log',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleDeleteCall = async (callId) => {
    try {
      await api.delete(`/api/call-logs/${callId}`);
      fetchCalls();
      toast({
        title: 'Call log deleted',
        status: 'success',
        duration: 3000,
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error deleting call log:', error);
      toast({
        title: 'Error deleting call log',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const startEditing = (call) => {
    setEditingCallId(call._id);
    setEditedNotes(call.notes);
  };

  const cancelEditing = () => {
    setEditingCallId(null);
    setEditedNotes('');
  };

  const getOutcomeColor = (outcome) => {
    switch (outcome) {
      case 'answered':
        return 'green';
      case 'not_answered':
        return 'red';
      case 'rescheduled':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  return (
    <VStack spacing={2} align="stretch">
      <Box borderWidth={1} p={2} borderRadius="md">
        <HStack spacing={2} align="flex-start">
          <FormControl flex={1} isRequired>
            <FormLabel fontSize="xs" mb={0}>Outcome</FormLabel>
            <RadioGroup 
              value={newCall.outcome} 
              onChange={(value) => setNewCall({ ...newCall, outcome: value })}
              size="sm"
            >
              <Flex direction="row" wrap="wrap" gap={2}>
                <Radio value="answered">Answered</Radio>
                <Radio value="not_answered">Not Answered</Radio>
                <Radio value="rescheduled">Rescheduled</Radio>
              </Flex>
            </RadioGroup>
          </FormControl>

          {newCall.outcome === 'rescheduled' && (
            <FormControl isRequired flex={1}>
              <FormLabel fontSize="xs" mb={0}>Reschedule To</FormLabel>
              <Input
                size="sm"
                type="datetime-local"
                value={newCall.rescheduledTo}
                onChange={(e) => setNewCall({ ...newCall, rescheduledTo: e.target.value })}
                min={new Date().toISOString().slice(0, 16)}
              />
            </FormControl>
          )}
        </HStack>

        <HStack mt={2} spacing={2}>
          <FormControl flex={1}>
            <FormLabel fontSize="xs" mb={0}>Notes</FormLabel>
            <Textarea
              size="sm"
              value={newCall.notes}
              onChange={(e) => setNewCall({ ...newCall, notes: e.target.value })}
              placeholder="Call notes..."
              rows={2}
            />
          </FormControl>

          <Button
            colorScheme="blue"
            onClick={handleAddCall}
            isLoading={isLoading}
            size="sm"
            alignSelf="flex-end"
          >
            Add
          </Button>
        </HStack>
      </Box>

      <Divider my={1} />

      <VStack spacing={2} align="stretch" maxH="300px" overflowY="auto">
        {calls.map((call) => (
          <Box
            key={call._id}
            p={2}
            borderWidth={1}
            borderRadius="md"
            position="relative"
          >
            <HStack justify="space-between" mb={1}>
              <HStack spacing={1}>
                <Badge colorScheme={getOutcomeColor(call.outcome)} fontSize="xs">
                  {call.outcome}
                </Badge>
                <Text fontSize="xs" color="blue.600">
                  {call.calledBy?.email || 'Unknown User'}
                </Text>
              </HStack>
              <HStack>
                {editingCallId === call._id ? (
                  <>
                    <IconButton
                      icon={<CheckIcon />}
                      size="xs"
                      variant="ghost"
                      colorScheme="green"
                      onClick={() => handleUpdateCall(call._id, { notes: editedNotes })}
                    />
                    <IconButton
                      icon={<CloseIcon />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={cancelEditing}
                    />
                  </>
                ) : (
                  <IconButton
                    icon={<DeleteIcon />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleDeleteCall(call._id)}
                  />
                )}
              </HStack>
            </HStack>

            {editingCallId === call._id ? (
              <Textarea
                size="sm"
                value={editedNotes}
                onChange={(e) => setEditedNotes(e.target.value)}
                mb={1}
                rows={2}
                autoFocus
              />
            ) : (
              <Box 
                onDoubleClick={() => startEditing(call)}
                cursor="pointer"
                p={1}
                borderRadius="sm"
                _hover={{ bg: "gray.50" }}
                mb={1}
              >
                <Text fontSize="xs">{call.notes}</Text>
              </Box>
            )}

            <HStack justify="space-between" alignItems="center">
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color="gray.500">
                  {new Date(call.timestamp).toLocaleString()}
                </Text>
                {call.rescheduledTo && (
                  <Text fontSize="xs" color="blue.500">
                    Rescheduled: {new Date(call.rescheduledTo).toLocaleString()}
                  </Text>
                )}
              </VStack>
              
              <RadioGroup
                size="xs"
                value={call.outcome}
                onChange={(value) => handleUpdateCall(call._id, { outcome: value })}
              >
                <Flex direction="row" wrap="wrap" gap={1}>
                  <Radio value="answered" size="xs">Answered</Radio>
                  <Radio value="not_answered" size="xs">Not Answered</Radio>
                  <Radio value="rescheduled" size="xs">Rescheduled</Radio>
                </Flex>
              </RadioGroup>
            </HStack>
          </Box>
        ))}
      </VStack>
    </VStack>
  );
};

export default CallsForm;
